import { type PartialDeep } from 'type-fest';

import { type CanvasElementFrom, type PropsOf } from '@blaze-runtime/engine-domain';
import { type PropertiesConfigInputColorModel } from '@blaze-shared/elements-domain';

import {
  getSourceProps,
  getSourceTransforms,
} from '../../canvas-elements/element-model/transformations';
import { parkedToggleButtonProps } from '../toggle-button/toggle-button.transforms';
type TagsElement = CanvasElementFrom<'tags'>;
type TagsProps = PartialDeep<PropsOf<'tags'>>;

function getBackgroundColor(fill?: PartialDeep<PropertiesConfigInputColorModel> | undefined): {
  inputFill: PartialDeep<PropertiesConfigInputColorModel>;
  listFill: PartialDeep<PropertiesConfigInputColorModel>;
} {
  const inputFill = { ...fill };
  const listFill = { ...fill };

  if (inputFill?.selectorType === 'none') {
    inputFill.selectorType = 'solid';
    inputFill.color1 = '$t.fillInternal';
  }

  if (listFill?.selectorType === 'none') {
    listFill.selectorType = 'solid';
    listFill.color1 = '$t.fill';
  }

  return {
    inputFill,
    listFill,
  };
}

function transformDropdownToTags(source: CanvasElementFrom<'dropdown'>): TagsElement {
  const props: PartialDeep<PropsOf<'dropdown'>> = source.props || {};
  const newProps: TagsProps = {
    content: {
      title: {
        enabled: props.content?.title?.enabled,
        label: props.content?.title?.label,
        position: props.content?.title?.position,
        align: props.content?.title?.align,
        width: props.content?.title?.width,
        icon: props.content?.title?.icon,
        iconPosition: props.content?.title?.iconPosition,
        tooltip: props.content?.title?.tooltip,
      },
      data: {
        defaultValue: props.content?.data?.defaultValue,
      },
      tags: {
        placeholder: props.content?.menu?.placeholder,
        values: props.content?.menu?.values,
        preselect: props.content?.menu?.preselect ? [props.content.menu.preselect] : undefined,
      },
      options: {
        canAddNew: undefined, // no mapping
        readonly: undefined, // no mapping
        singleSelect: undefined, // no mapping
      },
    },
    style: {
      fontSize: props.style?.fontSize,
      general: {
        title: props.style?.general?.title,
        accent: props.style?.general?.accent,
      },
      input: {
        color: props.style?.input?.color,
        fill: props.style?.input?.fill,
        border: props.style?.input?.border,
        radius: props.style?.input?.radius,
      },
      list: {
        value: props.style?.list?.value,
        background: props.style?.list?.background,
        border: props.style?.list?.border,
        radius: props.style?.list?.radius,
      },
      tags: {
        palette: undefined, // no mapping
      },
    },
  };

  return { props: newProps } as any;
}

function transformRadioToTags(source: CanvasElementFrom<'radio'>): TagsElement {
  const props: PartialDeep<PropsOf<'radio'>> = source.props || {};
  const newProps: TagsProps = {
    content: {
      title: {
        enabled: props.content?.title?.enabled,
        label: props.content?.title?.label,
        position: props.content?.title?.position,
        align: props.content?.title?.align,
        width: props.content?.title?.width,
        icon: props.content?.title?.icon,
        iconPosition: props.content?.title?.iconPosition,
        tooltip: props.content?.title?.tooltip,
      },
      data: {
        defaultValue: props.content?.data?.defaultValue,
      },
      tags: {
        placeholder: undefined, // no mapping
        values: props.content?.radio?.values,
        preselect: props.content?.radio?.preselect ? [props.content.radio.preselect] : undefined,
      },
      options: {
        canAddNew: undefined, // no mapping
        readonly: props.content?.radio?.readonly,
        singleSelect: true, // no mapping
      },
    },
    style: {
      fontSize: props.style?.fontSize,
      general: {
        title: props.style?.general?.title,
        accent: props.style?.general?.accent,
      },
      input: {
        color: props.style?.radio?.label,
        fill: props.style?.radio?.fill,
        border: props.style?.radio?.border,
        radius: undefined, // no mapping
      },
      list: {
        value: props.style?.radio?.label,
        background: props.style?.radio?.fill,
        border: props.style?.radio?.border,
        radius: undefined, // no mapping
      },
      tags: {
        palette: undefined, // no mapping
      },
    },
  };

  return { props: newProps } as any;
}

function transformCheckboxToTags(source: CanvasElementFrom<'checkbox'>): TagsElement {
  const props: PartialDeep<PropsOf<'checkbox'>> = source.props || {};
  const newProps: TagsProps = {
    content: {
      title: {
        enabled: props.content?.title?.enabled,
        label: props.content?.title?.label,
        position: props.content?.title?.position,
        align: props.content?.title?.align,
        width: props.content?.title?.width,
        icon: props.content?.title?.icon,
        iconPosition: props.content?.title?.iconPosition,
        tooltip: props.content?.title?.tooltip,
      },
      data: {
        defaultValue: props.content?.data?.defaultValue,
      },
      tags: {
        placeholder: undefined, // no mapping
        values: props.content?.checkbox?.values,
        preselect: props.content?.checkbox?.preselect,
      },
      options: {
        canAddNew: undefined, // no mapping
        readonly: props.content?.checkbox?.readonly,
        singleSelect: undefined, // no mapping
      },
    },
    style: {
      fontSize: props.style?.fontSize,
      general: {
        title: props.style?.general?.title,
        accent: props.style?.general?.accent,
      },
      input: {
        color: props.style?.checkbox?.label,
        fill: props.style?.checkbox?.fill,
        border: props.style?.checkbox?.border,
        radius: props.style?.checkbox?.radius,
      },
      list: {
        value: props.style?.checkbox?.label,
        background: props.style?.checkbox?.fill,
        border: props.style?.checkbox?.border,
        radius: undefined, // no mapping
      },
      tags: {
        palette: undefined, // no mapping
      },
    },
  };

  return { props: newProps } as any;
}

function transformTextFieldToTags(source: CanvasElementFrom<'textField'>): TagsElement {
  const props: PartialDeep<PropsOf<'textField'>> = source.props || {};
  const { inputFill, listFill } = getBackgroundColor(props.style?.input?.fill);

  const newProps: TagsProps = {
    content: {
      title: {
        enabled: props.content?.title?.enabled,
        label: props.content?.title?.label,
        position: props.content?.title?.position,
        align: props.content?.title?.align?.horizontal,
        width: props.content?.title?.width,
        icon: props.content?.title?.icon,
        iconPosition: props.content?.title?.iconPosition,
        tooltip: props.content?.title?.tooltip,
      },
      data: {
        defaultValue: props.content?.input?.placeholder ? 'placeholder' : undefined,
      },
      tags: {
        placeholder: props.content?.input?.placeholder,
        values: undefined, // no mapping
        preselect: undefined, // no mapping
      },
      options: {
        canAddNew: undefined, // no mapping
        readonly: undefined, // no mapping
        singleSelect: undefined, // no mapping
      },
    },
    style: {
      fontSize: props.style?.fontSize,
      general: {
        title: props.style?.general?.title,
        accent: props.style?.general?.accent,
      },
      input: {
        color: props.style?.input?.value,
        fill: inputFill,
        border: props.style?.input?.border,
        radius: props.style?.input?.radius,
      },
      list: {
        value: props.style?.input?.value,
        background: listFill,
        border: props.style?.input?.border,
        radius: props.style?.input?.radius,
      },
      tags: {
        palette: undefined, // no mapping
      },
    },
  };

  return { props: newProps } as any;
}

function transformTextInputToTags(source: CanvasElementFrom<'textInput'>): TagsElement {
  const props: PartialDeep<PropsOf<'textInput'>> = source.props || {};
  const newProps: TagsProps = {
    content: {
      title: {
        enabled: props.content?.title?.enabled,
        label: props.content?.title?.label,
        position: props.content?.title?.position,
        align: props.content?.title?.align?.horizontal,
        width: props.content?.title?.width,
        icon: props.content?.title?.icon,
        iconPosition: props.content?.title?.iconPosition,
        tooltip: props.content?.title?.tooltip,
      },
      data: {
        defaultValue: props.content?.input?.placeholder ? 'placeholder' : undefined,
      },
      tags: {
        placeholder: props.content?.input?.placeholder,
        values: undefined, // no mapping
        preselect: undefined, // no mapping
      },
      options: {
        canAddNew: undefined, // no mapping
        readonly: props.content?.input?.readonly,
        singleSelect: undefined, // no mapping
      },
    },
    style: {
      fontSize: props.style?.fontSize,
      general: {
        title: props.style?.general?.title,
        accent: props.style?.general?.accent,
      },
      input: {
        color: props.style?.input?.value,
        fill: props.style?.input?.fill,
        border: props.style?.input?.border,
        radius: props.style?.input?.radius,
      },
      list: {
        value: props.style?.input?.value,
        background: props.style?.input?.fill,
        border: props.style?.input?.border,
        radius: props.style?.input?.radius,
      },
      tags: {
        palette: undefined, // no mapping
      },
    },
  };

  return { props: newProps } as any;
}

function transformToggleButtonToTags(source: CanvasElementFrom<'toggleButton'>): TagsElement {
  const props = getSourceProps(source);
  const propsT = getSourceTransforms<'toggleButton', 'tags'>(source, 'tags');
  const newProps: TagsProps = {
    content: {
      title: { ...props.content?.title },
      tags: {
        placeholder: propsT?.content?.tags?.placeholder,
        values: propsT?.content?.tags?.values,
        preselect: propsT?.content?.tags?.preselect,
      },
      options: {
        canAddNew: propsT?.content?.options?.canAddNew,
        readonly: propsT?.content?.options?.readonly,
        singleSelect: propsT?.content?.options?.singleSelect,
      },
    },
    style: {
      fontSize: propsT?.style?.fontSize,
      general: {
        title: props.style?.general?.title,
        accent: propsT?.style?.general?.accent,
      },
      input: {
        color: propsT?.style?.input?.color,
        fill: propsT?.style?.input?.fill,
        border: propsT?.style?.input?.border,
        radius: propsT?.style?.input?.radius,
      },
    },
    transforms: {
      toggleButton: parkedToggleButtonProps(props),
    },
  };

  return { props: newProps } as unknown as TagsElement;
}

export const transformToTags = {
  transformDropdownToTags,
  transformRadioToTags,
  transformCheckboxToTags,
  transformTextFieldToTags,
  transformTextInputToTags,
  transformToggleButtonToTags,
};
